import { ref, watch } from "vue";

import { fetchSignedFileUrlOrThrow } from "@/api/fileSignedUrl";
import { useFileOpen } from "@/composables/useFileOpen";
import { useLayoutStore } from "@/store/layoutStore";
import { showToastForError } from "@/utils/toast";

/**
 * The preview bar's signed URL, fetched each time the bar is shown or its file changes, so it never
 * holds an expired one; and opening the bar's file in the overlay.
 */
export function usePreviewBarFile() {
  const layoutStore = useLayoutStore();
  const { openPreviewOverlay } = useFileOpen();
  const fileUrl = ref("");
  let isExpanding = false;

  watch(
    () =>
      [
        layoutStore.bars.previewBar.visible,
        layoutStore.previewFile.objectName,
        layoutStore.previewFile.generation,
      ] as const,
    async ([visible, objectName, generation], _, onCleanup) => {
      fileUrl.value = "";
      if (!visible || !objectName) return;
      let stale = false;
      onCleanup(() => (stale = true));
      try {
        const url = await fetchSignedFileUrlOrThrow(objectName, generation);
        if (!stale) fileUrl.value = url;
      } catch (error) {
        if (!stale) showToastForError(error);
      }
    },
    { immediate: true },
  );

  async function expandPreview() {
    if (isExpanding) return;
    isExpanding = true;
    try {
      await openPreviewOverlay(layoutStore.previewFile);
    } finally {
      isExpanding = false;
    }
  }

  return { fileUrl, expandPreview };
}
